import { useState, useRef } from "react";
import { Icon } from "@/components/Icon";
import { iconNames } from "@/components/icons";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import type { FileUploadProgress } from "@/hooks/useFileUpload";

const DEFAULT_ACCEPTED_TYPES = ".jpg,.jpeg,.png,.webp,.heic";

export interface KiiImageUploadZoneProps {
  onUpload: (files: File[]) => void;
  uploadProgress: FileUploadProgress[];
  isUploading: boolean;
  accept?: string;
}

export function KiiImageUploadZone({
  onUpload,
  uploadProgress,
  isUploading,
  accept = DEFAULT_ACCEPTED_TYPES,
}: KiiImageUploadZoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onUpload(Array.from(files));
    }
    // Reset input so same file can be selected again
    e.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const imageFiles = Array.from(files).filter(
        (f) => /\.(jpe?g|png|webp|heic)$/i.test(f.name) || f.type.startsWith("image/"),
      );
      if (imageFiles.length > 0) {
        onUpload(imageFiles);
      }
    }
  };

  return (
    <div>
      <div
        className={cn(
          "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
          isDragOver
            ? "border-primary bg-primary/5"
            : "border-border hover:border-primary/50 hover:bg-muted/30",
          isUploading && "pointer-events-none opacity-60",
        )}
        onClick={() => !isUploading && fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Icon
          name={iconNames.cloudUpload}
          size={32}
          className="mx-auto mb-2 text-muted-foreground"
        />
        <p className="text-sm text-foreground font-medium">
          {isDragOver ? "Drop photos here" : "Click to browse or drag photos here"}
        </p>
        <p className="text-xs text-muted-foreground mt-1">Accepts JPG, PNG, WebP, HEIC</p>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Upload Progress */}
      {uploadProgress.length > 0 && (
        <div className="mt-3 space-y-2">
          {uploadProgress.map((fp) => (
            <div key={fp.filename} className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground truncate flex-1 min-w-0">
                {fp.filename}
              </span>
              <div className="w-24 flex-shrink-0">
                <Progress value={fp.progress} className="h-2" />
              </div>
              <span className="text-xs text-muted-foreground w-8 text-right flex-shrink-0">
                {fp.status === "done" ? (
                  <Icon name={iconNames.check} size={14} className="text-green-500 inline" />
                ) : fp.status === "error" ? (
                  <Icon name={iconNames.error} size={14} className="text-destructive inline" />
                ) : (
                  `${fp.progress}%`
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
